import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  ExportRiskMetricGroupCommand,
  type ExportRiskMetricGroupCommandInput,
  type ExportRiskMetricGroupCommandOutput,
} from '@amzn/gbsa-helios-typescript-client';

import { apiHeliosClient } from 'src/v2/shared/data';
import { RiskScoreQueryKeys } from './keys';

/**
 * Generates a CSV export of ONE metric group — a root KPI (e.g. FPY, TPH) and all of its sub-metric
 * drivers — for a single station and time window, and returns a short-lived presigned S3 download
 * URL plus generation metadata (`downloadUrl`, `recordCount`, `generatedAt`, `expiresAt`). This is
 * the drill-down tree `GetRiskMetricGroup` serves, materialized server-side as a CSV.
 *
 * User-triggered and non-cached, so this is a mutation rather than a cached query — the presigned
 * URL expires quickly, so there is nothing worth keeping in the query cache. No `withRetry`: the
 * export is a server-side generate, not an idempotent read, so a failure surfaces for the user to
 * retry explicitly rather than being retried under the hood.
 */
export function useExportRiskMetricGroupMutation(
  options?: Omit<
    UseMutationOptions<
      ExportRiskMetricGroupCommandOutput,
      Error,
      ExportRiskMetricGroupCommandInput
    >,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<
  ExportRiskMetricGroupCommandOutput,
  Error,
  ExportRiskMetricGroupCommandInput
> {
  return useMutation({
    ...options,
    mutationKey: [RiskScoreQueryKeys.metricGroupExports],
    mutationFn: (input: ExportRiskMetricGroupCommandInput) =>
      apiHeliosClient.send(new ExportRiskMetricGroupCommand(input)),
  });
}
